import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ResponsibleAvatars.css";

const getId = (u) => u?.user_id ?? u?.id ?? null;
const getName = (u) => u?.user_name ?? u?.name ?? "-";
const getHandle = (u) => u?.username ?? (getId(u) != null ? String(getId(u)) : "");

const firstLetter = (name) => {
  const s = String(name || "").trim();
  if (!s) return "?";
  const w = s.split(/\s+/)[0];
  return (w[0] || "?").toUpperCase()
};

const hashColorClass = (idOrName) => {
  const s = String(idOrName ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return `resp-avatar--c${(h % 8) + 1}`;
};

function useOutsideClick(refs, onOutside) {
  useEffect(() => {
    const onDown = (e) => {
      const hit = refs.some((r) => r?.current && r.current.contains(e.target));
      if (!hit) onOutside?.();
    };

    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);

    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
    };
  }, [refs, onOutside]);
}

function Popover({ open, anchorEl, onClose, children }) {
  const popRef = useRef(null);
  useOutsideClick([popRef, { current: anchorEl }], () => {
    if (open) onClose?.();
  });

  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

  useEffect(() => {
    if (!open || !anchorEl) return;

const calc = () => {
  const r = anchorEl.getBoundingClientRect();
  const popoverWidth = 340;
  // прижимаем правый край popover к правому краю аватара
  let left = r.right - popoverWidth;
  // не уходить за левый край
  left = Math.max(12, left);
  // не уходить за правый край
  left = Math.min(window.innerWidth - popoverWidth - 12, left);

  const top = Math.min(window.innerHeight - 200, r.bottom + 8);

  setPos({ top, left, ready: true });
};

    calc();
    window.addEventListener("resize", calc);
    window.addEventListener("scroll", calc, true);

    return () => {
      window.removeEventListener("resize", calc);
      window.removeEventListener("scroll", calc, true);
    };
  }, [open, anchorEl]);

  useEffect(() => {
    if (!open) return;

    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={popRef}
      className="resp-popover"
      style={{
        top: pos.top,
        left: pos.left,
        opacity: pos.ready ? 1 : 0,
        transform: pos.ready ? "translateY(0)" : "translateY(-2px)",
      }}
      role="dialog"
      aria-modal="false"
    >
      {children}
    </div>,
    document.body
  );
}

export default function ResponsibleAvatars({
  items = [],
  canManage = false,
  onRemove,
  onViewProfile,
  max = 4,
  showAdd = false,
  addTitle = "Добавить",
  onAdd,
  label,
}) {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const shown = list.slice(0, max);
  const extra = Math.max(0, list.length - shown.length);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [anchor, setAnchor] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);

  const openMenu = (user, el) => {
    setActive(user);
    setAnchor(el);
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setActive(null);
    setAnchor(null);
  };

  const openConfirm = (user) => {
    setConfirmUser(user);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmUser(null);
  };

  const handleViewProfile = (user) => {
    if (!user) return;
    onViewProfile?.(user);
  };

  const activeName = getName(active);
  const activeHandle = getHandle(active);

  return (
    <div className="resp-inline" aria-label="responsibles">
      {label ? <div className="resp-label">{label}</div> : null}

      <div className="resp-stack">
        {shown.map((u) => {
          const id = getId(u);
          const name = getName(u);
          const letter = firstLetter(name);
          const cls = hashColorClass(id ?? name);

          return (
            <button
              key={String(id ?? name)}
              type="button"
              className={`resp-avatar ${cls}`}
              title={name}
              aria-label={name}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openMenu(u, e.currentTarget);
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {letter}
            </button>
          );
        })}

        {extra > 0 ? (
          <span className="resp-more" title={list.slice(max).map(getName).join(", ")}>
            +{extra}
          </span>
        ) : null}

        {showAdd ? (
          <button
            type="button"
            className="resp-add"
            title={addTitle}
            aria-label={addTitle}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAdd?.(e);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            +
          </button>
        ) : null}
      </div>

      <Popover open={open} anchorEl={anchor} onClose={closeMenu}>
        <div className="resp-popover__header">
          <button
            className="resp-popover__close"
            type="button"
            aria-label="Close"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closeMenu();
            }}
          >
            ×
          </button>

          <div className="resp-popover__headerRow">
            <div className="resp-popover__bigAvatar">{firstLetter(activeName)}</div>

            <div className="resp-popover__headerMeta">
              <div className="resp-popover__title">{activeName}</div>
              <div className="resp-popover__subtitle">
                {activeHandle ? `@${activeHandle}` : "\u00A0"}
              </div>
            </div>
          </div>
        </div>

        <div className="resp-popover__menu">
          <button
            type="button"
            className="resp-menuItem"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleViewProfile(active);
              closeMenu();
            }}
          >
            Посмотреть профиль
          </button>

          <button
            type="button"
            className="resp-menuItem resp-menuItem--danger"
            disabled={!canManage}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (!canManage || !active) return;

              const userToDelete = active;
              closeMenu();

              setTimeout(() => {
                openConfirm(userToDelete);
              }, 0);
            }}
          >
            Удалить из карточки
          </button>
        </div>
      </Popover>

      {confirmOpen
        ? createPortal(
            <div
              className="resp-confirm__backdrop"
              onClick={closeConfirm}
            >
              <div
                className="resp-confirm__card"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="resp-confirm__title">Удалить пользователя?</div>

                <div className="resp-confirm__text">
                  Вы действительно хотите удалить{" "}
                  <b>{confirmUser ? getName(confirmUser) : "пользователя"}</b> из карточки?
                </div>

                <div className="resp-confirm__actions">
                  <button
                    type="button"
                    className="resp-confirm__btn resp-confirm__btn--danger"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const uid = getId(confirmUser);
                      if (uid != null) onRemove?.(uid);

                      closeConfirm();
                    }}
                  >
                    Да, удалить
                  </button>

                  <button
                    type="button"
                    className="resp-confirm__btn"
                    onClick={closeConfirm}
                  >
                    Нет
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}